/**
 * Section Renderer (Alpine.morph-based)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Wraps Shopify's Section Rendering API with surgical DOM morphing via
 * `Alpine.morph()`. Unchanged subtrees, input focus, in-flight Alpine state,
 * and selected/checked attributes are preserved across re-renders.
 *
 * Why Alpine.morph (vs full innerHTML / replaceWith):
 *   - Preserves Alpine x-data state (cart-discount-code, qty inputs, etc.)
 *   - Preserves focus and caret position in active inputs
 *   - Preserves checked / selected state on form elements
 *   - Preserves <details>, <dialog>, and other open/close UI state
 *   - Diffs are minimal — only changed nodes are touched
 *
 * Usage from JS:
 *   import { sectionRenderer, morphSection } from './section-renderer.js';
 *   await sectionRenderer.renderSection('cart-drawer-items'); // fetch + morph
 *
 * Or via the global (for IIFE / non-module callers):
 *   await window.Theme.sectionRenderer.renderSection('cart-drawer-items');
 *
 * Adapted from `shopify-theme-stack-1/assets/section-renderer.js`.
 */

// Ensure window.Theme exists for global exposure
window.Theme = window.Theme || {};

const SECTION_ID_PREFIX = 'shopify-section-';

// The morph is generic: components opt into behavior with markup sentinels
// described in docs/components.md under section updates:
//   data-skip-subtree-update  — reactive island: never touch this element or
//                               its children (Alpine keeps it in sync).
//   data-skip-wrapper-update  — runtime-owned shell (dialogs, header): never
//                               patch this element's OWN attributes/classes
//                               (JS toggles data-open / hidden / data-scrolled
//                               on it at runtime and SSR only knows the closed
//                               state), but DO morph its children.
//   data-line-key / data-morph-key — stable identity for repeated nodes.
// No component names or attribute lists belong in this file.

class SectionRenderer {
  /** @type {Map<string, string>} Cache of rendered section HTML keyed by URL */
  #cache = new Map();

  /** @type {Map<string, AbortController>} In-flight aborts keyed by section id */
  #abortControllersBySectionId = new Map();

  /** @type {Map<string, Promise<string>>} Dedup pending fetches keyed by URL */
  #pendingPromises = new Map();

  constructor() {
    // Pre-cache the SSR HTML of every section already on the page.
    // Lets the first re-render skip the network round-trip when content
    // is unchanged (cache hit) — useful in design mode and quick toggles.
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Fetch a section's HTML and morph it into the live DOM.
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache] - Use cached HTML if present (default: true outside design mode)
   * @param {URL} [options.url] - URL to render the section against (default: current page)
   * @returns {Promise<string>} The fetched section HTML
   */
  async renderSection(sectionId, options) {
    const designMode = !!(window.Shopify && window.Shopify.designMode);
    const { cache = !designMode } = options || {};
    const { url } = options || {};

    this.#abortPendingMorph(sectionId);
    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      morphSection(sectionId, sectionHTML);
    }
    return sectionHTML;
  }

  #abortPendingMorph(sectionId) {
    const existing = this.#abortControllersBySectionId.get(sectionId);
    if (existing) existing.abort();
  }

  /**
   * Fetch (or return cached) HTML for a section.
   * @param {string} sectionId
   * @param {boolean} useCache
   * @param {URL} [url]
   * @returns {Promise<string>}
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pending = this.#pendingPromises.get(sectionUrl);
    if (pending) return pending;

    if (useCache) {
      const cached = this.#cache.get(sectionUrl);
      if (cached) return cached;
    }

    pending = fetch(sectionUrl).then((res) => res.text());
    this.#pendingPromises.set(sectionUrl, pending);

    const html = await pending;
    this.#pendingPromises.delete(sectionUrl);
    this.#cache.set(sectionUrl, html);
    return html;
  }

  /** Clear the URL → HTML cache (e.g. after a known content change). */
  clearCache() {
    this.#cache.clear();
  }

  #cachePageSections() {
    try {
      for (const section of document.querySelectorAll('.shopify-section')) {
        const url = buildSectionRenderingURL(section.id);
        if (this.#cache.get(url)) continue;
        if (containsShadowRoot(section)) continue;
        this.#cache.set(url, section.outerHTML);
      }
    } catch (_) {}
  }
}

function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  // Use ?section_id=<id> form (single section, raw HTML response).
  // Avoids the JSON wrapping of the multi-section ?sections= form.
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}

export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
}

export function normalizeSectionId(sectionId) {
  return String(sectionId || '').replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

function containsShadowRoot(element) {
  if (!element || !element.children) return !!(element && element.shadowRoot);
  if (element.shadowRoot) return true;
  return Array.from(element.children).some(containsShadowRoot);
}

/**
 * Morph the existing section element into the new section markup.
 * @param {string} sectionId
 * @param {string} html
 */
export function morphSection(sectionId, html, { enterKeys = [] } = {}) {
  const enterSet = new Set(enterKeys);
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const selector = buildSectionSelector(sectionId);
  const existing = document.getElementById(selector);
  const next = fragment.getElementById(selector);

  if (!existing) {
    try { console.warn('[section-renderer] Existing section not found:', selector); } catch (_) {}
    return;
  }
  if (!next) {
    try { console.warn('[section-renderer] Section not found in response:', selector); } catch (_) {}
    return;
  }

  if (typeof Alpine !== 'undefined' && typeof Alpine.morph === 'function') {
    Alpine.morph(existing, next, {
      updating(el, toEl, childrenOnly, skip) {
        // Skip Shopify Theme Editor's section-rendered marker comment node
        if (toEl.nodeType === Node.COMMENT_NODE
            && toEl.nodeValue === 'shopify:rendered_by_section_api') {
          skip();
          return;
        }
        // Reactive-island sentinel (issue #20 — mirrors shopify-framework
        // ADR-0001 / update-strategies.md). A subtree carrying
        // data-skip-subtree-update is a pure derivation of $store.cart with
        // continuous client state (in-flight inputs, focus, optimistic toggles,
        // animations). Read the sentinel on the LIVE node and skip it entirely:
        // the reactive layer keeps it in sync with the store; morphing in the
        // incoming SSR HTML would clobber that state.
        if (el instanceof HTMLElement && el.hasAttribute('data-skip-subtree-update')) {
          skip();
          return;
        }
        // Runtime-owned shell: JS is the only writer of this element's own
        // attributes/classes (dialog open state + hidden class, header scroll
        // flags). Patching them from SSR markup — which only knows the closed
        // state — leaks state (e.g. re-baking `hidden` onto an open drawer
        // made the next close skip its slide-out). Children still morph.
        // Trade-off, accepted knowingly: a Liquid-computed attribute on such
        // a wrapper never refreshes via morph — its SSR value is final.
        if (el instanceof HTMLElement && el.hasAttribute('data-skip-wrapper-update')) {
          childrenOnly();
          return;
        }
      },
      updated(el) {
        // Re-run the web-component update lifecycle if the element exposes one
        if (el instanceof HTMLElement && typeof el.updatedCallback === 'function') {
          queueMicrotask(() => el.updatedCallback());
        }
      },
      added(el) {
        // Cart lines the shopper just ADDED enter with a token fade/rise — the
        // one animated thing on the morph path (docs/components.md); price
        // and count patches stay instant by doctrine. Gated on `enterKeys`
        // (the keys add.js returned, threaded through by the morph adapter):
        // a line the morph re-inserts for any other reason — quantity change,
        // a Cart Transform handing a line back under a new key — must not
        // rise again. Double rAF: first frame paints the .morph-enter start
        // state, second removes it so the transition runs.
        if (!(el instanceof HTMLElement) || !el.matches || !el.matches('[data-line-key]')) return;
        if (!enterSet.has(el.getAttribute('data-line-key'))) return;
        el.classList.add('morph-enter');
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('morph-enter')));
      },
      key(el) {
        // Cart line items use data-line-key for stable identity across morphs
        // (preserves qty input focus, in-flight loading classes, etc.).
        if (el && el.getAttribute) {
          const lineKey = el.getAttribute('data-line-key');
          if (lineKey) return `line:${lineKey}`;
          // Generic identity for any repeated, Alpine-bearing node (e.g. upsell
          // cells). Without a key, morph matches siblings by position and only
          // patches attributes — an x-data component then keeps the OLD state
          // behind NEW markup (an "Add" button adding the previous product).
          const morphKey = el.getAttribute('data-morph-key');
          if (morphKey) return `key:${morphKey}`;
        }
        return el && el.id ? el.id : null;
      },
    });
  } else {
    // Fallback if Alpine.morph isn't loaded yet (rare — plugin script defers
    // alongside Alpine itself). Replace the children to avoid losing the
    // section wrapper id.
    try { existing.innerHTML = next.innerHTML; } catch (_) {}
  }
}

/**
 * Morph multiple sections from a `?sections=` JSON response.
 * @param {Object<string, string>} sectionsHtml
 */
export function morphSections(sectionsHtml, options = {}) {
  if (!sectionsHtml || typeof sectionsHtml !== 'object') return;
  Object.entries(sectionsHtml).forEach(([id, html]) => morphSection(id, html, options));
}

export const sectionRenderer = new SectionRenderer();

// Global exposure for IIFE / non-module callers
window.Theme.morphSection = morphSection;
window.Theme.morphSections = morphSections;
window.Theme.sectionRenderer = sectionRenderer;

// No automatic event subscriptions: callers (e.g. cart-drawer.js) decide when
// to re-render. Variant changes are handled by inline x-data components
// reactively via the `store:variant-changed` window event — they don't need a
// section morph, and triggering one here was breaking the product gallery.
